import { Box, Center, Text } from '@mantine/core'
import type { CSSProperties, ReactNode } from 'react'

import { HydrationSafe } from '../hooks/HydrationSafe'
import NoQuoteLockIcon from './NoQuoteLockIcon'

/**
 * Port of cdc-web `Compact/_shared/RedesignedPriceCellShell.tsx`。
 * 裁剪：TwoWayCard 只有 moneyline（无 spread/total 的 LineLabel 行），但 shell
 * 的 ghost-sizing 技巧原样保留（锁定/激活两种状态宽度一致、内容不撑跳）。
 */

export const PRICE_CELL_MIN_WIDTH = 56
export const PRICE_CELL_HEIGHT = 44
export const PRICE_CELL_RADIUS = 8

const SHARED_BOX: CSSProperties = {
  width: '100%',
  minWidth: PRICE_CELL_MIN_WIDTH,
  border: '1px solid var(--border-surface-default)',
  borderRadius: PRICE_CELL_RADIUS,
  position: 'relative',
  flexShrink: 0,
}

/**
 * Ghost-sizing trick: a 1×1 grid stacks an invisible "sizing" copy of the label
 * (worst-case width) under the visible content, so the cell never resizes when
 * the quoted price ticks between e.g. "54%" and "9%" — the box stays at its
 * widest historical width within a render.
 */
function CellContent({
  label,
  sizingLabel,
}: Readonly<{ label: ReactNode; sizingLabel: ReactNode }>) {
  return (
    <Box
      style={{
        display: 'grid',
        gridTemplateAreas: '"cell"',
        alignItems: 'center',
        justifyItems: 'center',
      }}
    >
      <Box
        aria-hidden
        style={{
          gridArea: 'cell',
          visibility: 'hidden',
          paddingInline: 8,
          whiteSpace: 'nowrap',
        }}
      >
        <Text variant="body-emphasis">{sizingLabel}</Text>
      </Box>
      <Box style={{ gridArea: 'cell' }}>{label}</Box>
    </Box>
  )
}

/** No-quote cell: centered lock glyph, whole cell is the tooltip hover target. */
export function LockedPriceCell({ sizingLabel = '—' }: { sizingLabel?: string } = {}) {
  return (
    <Center
      data-contract-btn
      data-locked
      h={PRICE_CELL_HEIGHT}
      style={{ ...SHARED_BOX, flexDirection: 'column' }}
    >
      <CellContent label={<NoQuoteLockIcon fillParent />} sizingLabel={sizingLabel} />
    </Center>
  )
}

interface ActivePriceCellShellProps {
  label: string
  /** 全卡最宽价格文本：跨格统一宽度（FutureCard 多行对齐用）。 */
  sizingLabel?: string
  onClick?: (e: React.MouseEvent<HTMLButtonElement>) => void
}

/** Quoted cell: a real button (click = 预留 stub，暂无跳转）。 */
export function ActivePriceCellShell({
  label,
  sizingLabel,
  onClick,
}: Readonly<ActivePriceCellShellProps>) {
  return (
    <Box
      component="button"
      type="button"
      data-contract-btn
      h={PRICE_CELL_HEIGHT}
      onClick={onClick}
      style={{
        ...SHARED_BOX,
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        overflow: 'hidden',
        background: 'transparent',
        cursor: 'pointer',
        appearance: 'none',
      }}
    >
      <HydrationSafe>
        <CellContent
          sizingLabel={sizingLabel ?? label}
          label={
            <Text
              variant="body-emphasis"
              c="var(--content-primary)"
              ta="center"
              suppressHydrationWarning
              style={{ whiteSpace: 'nowrap' }}
            >
              {label}
            </Text>
          }
        />
      </HydrationSafe>
    </Box>
  )
}
